import { env } from '@ria-client/env';
import pino, { type Logger, type LoggerOptions } from 'pino';
import { defaultBindings } from './constants';

export type LoggerType = Logger<'critical'>;

type Bindings = {
  component: string;
  path: string;
} & pino.Bindings;

/**
 * Creates and returns a Pino logger instance with specified bindings.
 * @param {Object} bindings - An object containing bindings for the logger.
 * @param {string} bindings.component - The component name to bind to the logger.
 * @param {string} bindings.path - The path to bind to the logger.
 * @param {Object<string, string>} bindings - Additional key-value pairs to bind to the logger.
 * @returns {Logger} A Pino logger instance configured with the specified bindings.
 */
export function loggerFactory(bindings: Bindings) {
  const bindingsWithDefault = { ...defaultBindings, ...bindings };
  const IS_DEV_ENV = env.NEXT_PUBLIC_DEPLOYABLE_ENV === 'dev';
  // Browser/Storybook/Chromatic have no `process` — same class of bug as unmocked env.
  const IS_EDGE = typeof process !== 'undefined' && process.env.NEXT_RUNTIME === 'edge';
  const level = IS_DEV_ENV ? 'trace' : (env.LOG_LEVEL ?? 'info');
  const options: LoggerOptions<'critical'> = {
    level,
    customLevels: {
      // `critical` is just an alias for `fatal`. This is required for DataDog to correctly parse and show "critical" logs.
      critical: 60,
    },
    formatters: {
      level(label) {
        return { level: label };
      },
      bindings() {
        return bindingsWithDefault;
      },
    },
    timestamp: pino.stdTimeFunctions.isoTime,
    // Call sites log `{ error }`, but pino only serializes Errors under `err`; without this an
    // Error (no enumerable fields) logged as `"error":{}`, hiding message, stack and cause.
    serializers: { error: pino.stdSerializers.errWithCause },
  };

  // This is required for correctly parsing edge logs.
  // Edge environments like NextJS middleware get detected as a browser rather than server side.
  // See https://github.com/pinojs/pino/issues/1613
  if (IS_EDGE) {
    return pino({
      ...options,
      serialize: true,
      browser: {
        // @ts-expect-error Pino types `obj` as `object`
        write: (obj: { level: string | number; [key: string]: string | number }) => {
          try {
            console.log(
              JSON.stringify({
                ...obj,
                ...bindingsWithDefault,
                level:
                  Number(obj.level) === options?.customLevels?.critical
                    ? 'critical'
                    : pino.levels.labels[Number(obj.level)],
              }),
            );
          } catch (err) {
            if (err instanceof Error) {
              console.log(
                JSON.stringify({
                  ...bindingsWithDefault,
                  name: err.name,
                  message: err.message,
                  stack: err.stack,
                }),
              );
            } else {
              console.log(
                JSON.stringify({ ...bindingsWithDefault, message: 'Unknown error type' }),
              );
            }
          }
        },
      },
    });
  }

  return pino(options);
}
